/**
 * Преобразует объект File в строку формата base64
 *
 * @description
 * Функция читает содержимое файла и конвертирует его в формат base64.
 * Использует FileReader для асинхронного чтения файла.
 * Возвращает результат в формате data URL.
 *
 * @param {File} file - Файл для конвертации
 * @returns {Promise<string>} Строка в формате data URL (data:mimetype;base64,...)
 *
 * @throws {Error} "Failed to read file as base64" - если не удалось прочитать файл
 * @throws {Error} "No file provided" - если файл не передан
 *
 * @example
 * ```typescript
 * // Для изображения
 * const file = new File(['...'], 'image.jpg', { type: 'image/jpeg' });
 * const base64 = await fileToBase64(file);
 * // Результат: data:image/jpeg;base64,/9j/4AAQSkZJRg...
 *
 * // Обработка ошибок
 * try {
 *   const base64 = await fileToBase64(file);
 * } catch (error) {
 *   console.error('Ошибка конвертации:', error);
 * }
 * ```
 */
export const fileToBase64 = (file: File): Promise<string> => {
  if (!file) {
    return Promise.reject(new Error("No file provided"));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to read file as base64"));
      }
    };

    reader.onerror = () => {
      reject(
        new Error(
          `File reading error: ${reader.error?.message || "Unknown error"}`,
        ),
      );
    };

    reader.readAsDataURL(file);
  });
};
